import { useState } from "react";
import { motion } from "framer-motion";
import { getPlatformIcon, detectPlatform, type Platform } from "./platformIcons";

interface SocialLinksBarProps {
  links: Record<string, string>;
}

export function SocialLinksBar({ links }: SocialLinksBarProps) {
  const [hoveredPlatform, setHoveredPlatform] = useState<string | null>(null);

  const socialLinks = Object.entries(links).map(([key, url]) => {
    const platform = key.startsWith("http") ? detectPlatform(key) : (key as Platform);
    const actualUrl = key.startsWith("http") ? key : url;
    return { platform, url: actualUrl, key };
  });

  if (socialLinks.length === 0) return null;

  return (
    <div className="relative flex items-center gap-0.5 px-1.5 py-1.5 rounded-2xl bg-[var(--color-bg)] border border-[var(--color-border)]">
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />

      {socialLinks.map(({ platform, url, key }) => {
        const Icon = getPlatformIcon(platform);
        const isHovered = hoveredPlatform === key;
        const displayName = platform.charAt(0).toUpperCase() + platform.slice(1);

        return (
          <a
            key={key}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex items-center justify-center size-10 rounded-xl transition-colors duration-200"
            onMouseEnter={() => setHoveredPlatform(key)}
            onMouseLeave={() => setHoveredPlatform(null)}
            aria-label={displayName}
          >
            <motion.span
              className="absolute inset-1 rounded-lg bg-[var(--color-primary)]/10"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{
                opacity: isHovered ? 1 : 0,
                scale: isHovered ? 1 : 0.9,
              }}
              transition={{ duration: 0.2 }}
            />

            <motion.span
              className="relative z-10"
              animate={{
                color: isHovered ? "var(--color-primary)" : "var(--color-text-muted)",
                scale: isHovered ? 1.1 : 1,
              }}
              transition={{ duration: 0.2 }}
            >
              <Icon className="size-[18px]" />
            </motion.span>

            <motion.span
              className="absolute bottom-1.5 left-1/2 -translate-x-1/2 h-[2px] rounded-full bg-[var(--color-primary)]"
              initial={{ width: 0, opacity: 0 }}
              animate={{
                width: isHovered ? 12 : 0,
                opacity: isHovered ? 1 : 0,
              }}
              transition={{ duration: 0.2 }}
            />

            <motion.span
              className="absolute -top-10 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-lg bg-[var(--color-text)] text-[var(--color-bg)] text-[11px] font-medium whitespace-nowrap pointer-events-none z-20"
              initial={{ opacity: 0, y: 4 }}
              animate={{
                opacity: isHovered ? 1 : 0,
                y: isHovered ? 0 : 4,
              }}
              transition={{ duration: 0.15 }}
            >
              {displayName}
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 size-2 rotate-45 bg-[var(--color-text)]" />
            </motion.span>
          </a>
        );
      })}
    </div>
  );
}
